import React, { useMemo, useState } from 'react';
import { Grid, Card, CardContent, Typography, Divider } from '@material-ui/core';
import { useForm, FormProvider } from 'react-hook-form';
import { vestResolver } from '@hookform/resolvers/vest';
import { useFormSnackbarError, useRecentCallback } from '@lib/common';
import { DealDetail } from '@lib/deals';
import { C3ValidationSuite } from './validators';
import {
  C3AddFormEntity,
  C3AddFormEntityPayload,
  Content,
  ContentEntity,
  contentEntityPayload,
} from './types';
import { C3PersonSelector } from './c3-person-selector';
import * as adapter from './form-adapter';
import { RelationFormActions } from './c3-form-actions';
import { ContentPersonRelationForm } from './content-person-relation';
import { SourceUrls } from '@pages/company-mapping/company-mapping-form/source-urls';

type C3FormProps = {
  onSubmit: (formData: C3AddFormEntityPayload) => any;
  onDelete?: (formData: C3AddFormEntity) => any;
  onCancel?: () => any;
  deal: C3AddFormEntity;
  createDeal?: boolean;
};

export function C3AddForm({
  deal,
  onSubmit,
  onDelete = null,
  onCancel = null,
  createDeal = false,
}: C3FormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formDeal = useMemo(() => adapter.toForm(deal), [deal]);
  const formRelationDefaults = useMemo(() => adapter.toForm(deal), [deal]);
  const [onDeleteFinish] = useRecentCallback(relation => onDelete(adapter.fromForm(relation)));

  const form = useForm({
    defaultValues: formDeal /* {
      sources: formDeal.source_url || " ",
      associated_person: formDeal.associated_person || [],
      content: formDeal.content || []
    } */,
    resolver: vestResolver(C3ValidationSuite),
    mode: 'onChange',
  });

  function toContentEntities(relations: any[] = []): contentEntityPayload[] {
    return relations
      .filter(rel => Object.keys(rel).length > 0) // remove empty objects
      .map(rel => ({
        content_vtr_id: rel.vtr_id,
        role: rel.role || '',
        sub_role: rel.sub_role || '',
        character: '',
      }));
  }

  const [onFinish] = useRecentCallback(async (formData: any) => {
    setIsSubmitting(true);
    try {
      const submitData: C3AddFormEntityPayload = {
        person_vtr_id: formData.associated_person?.[0]?.vtr_id || '',
        associated_content: toContentEntities(formData.contents), // 🔥 map back here
        source_links: formData.source_urls.join(',') || '',
        source: formData.source,
      };
      await onSubmit(submitData);
    } finally {
      setIsSubmitting(false);
    }
  });
  const formError = useFormSnackbarError();
  const submitForm = form.handleSubmit(onFinish, formError);
  const submitDeleteForm = () => onDeleteFinish(form.getValues());

  const actions = (
    <RelationFormActions
      relationData={formRelationDefaults}
      onDelete={submitDeleteForm}
      createRelation={createDeal}
      submitForm={submitForm}
      onCancel={onCancel}
    />
  );
  const actionsBottom = (
    <RelationFormActions
      relationData={formRelationDefaults}
      onDelete={submitDeleteForm}
      createRelation={createDeal}
      submitForm={submitForm}
      onCancel={onCancel}
      hidePublishSource={true}
    />
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={event => (event.preventDefault(), submitForm)}>
        <Grid container spacing={3}>
          <Grid item style={{ width: '100%' }}>
            {actions}
            <br />
            <Divider />
          </Grid>

          {/* Select Person Section */}
          <Grid item xs={12}>
            <C3PersonSelector deal={deal} createDeal={createDeal} />
          </Grid>

          <Grid item xs={12}>
            <ContentPersonRelationForm
              relationData={formRelationDefaults}
              createRelation={createDeal}
            />
          </Grid>
          {/* Sources Section */}
          <Grid item xs={12}>
            <SourceUrls
              source_urls={
                formRelationDefaults.source_urls ? formRelationDefaults.source_urls : ''
                // deal.source_url ? deal.source_url : ''
              }
            />
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Divider />
            <br />
            {actionsBottom}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}
